import React from "react";
import FontAwesome from "react-fontawesome";
import { cx } from "./util";
import { tooltipProps } from "../../util/tooltip";

// Complete literal classname maps — Tailwind's scanner needs full strings,
// and it keeps the emitted surface greppable.
const BUTTON_STYLE: Record<string, string> = {
  // btn-default lives in bootstrap-compat.css (honey bg per the original
  // theme's $btn-default-*), shared with the raw-classname consumers.
  default: "btn-default",
  primary: "btn-primary",
  danger: "btn-error",
  link: "btn-link",
};
const BUTTON_SIZE: Record<string, string> = {
  large: "btn-lg",
  small: "btn-sm",
  xsmall: "btn-xs",
};
const ALERT_STYLE: Record<string, string> = {
  info: "alert-info",
  warning: "alert-warning",
  danger: "alert-error",
  success: "alert-success",
};

type ButtonProps = {
  bsStyle?: string;
  bsSize?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

export function Button({
  bsStyle = "default",
  bsSize,
  className,
  type,
  href,
  children,
  title,
  ...rest
}: ButtonProps) {
  const classes = cx(
    "btn",
    BUTTON_STYLE[bsStyle] || BUTTON_STYLE.default,
    bsSize && BUTTON_SIZE[bsSize],
    className,
  );
  // Render `title` as a react-tooltip anchor instead of an HTML title so the
  // hint also shows on mobile taps. Mirror string titles into aria-label:
  // the old HTML `title` was the accessible name of icon-only buttons (the
  // whole chart toolbar), and data-tooltip-content contributes nothing to
  // the ARIA name (WCAG 4.1.2).
  const tip =
    typeof title === "string"
      ? { ...tooltipProps(title), "aria-label": title }
      : tooltipProps(title);
  if (href) {
    return (
      <a href={href} role="button" className={classes} {...tip} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <button type={type || "button"} className={classes} {...tip} {...rest}>
      {children}
    </button>
  );
}

export function ButtonToolbar({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="toolbar"
      className={cx("flex flex-wrap gap-2", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

type AlertProps = {
  bsStyle?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export function Alert({
  // Default per react-bootstrap 0.31 defaultProps (verified in Task 2 Step 1).
  bsStyle = "info",
  className,
  children,
  ...rest
}: AlertProps) {
  return (
    <div
      role="alert"
      className={cx(
        "alert",
        ALERT_STYLE[bsStyle] || ALERT_STYLE.info,
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Badge({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>) {
  // Bootstrap 3 badges were pills: #777-ish bg, white text, fully rounded.
  // badge-neutral + rounded-full is the daisyUI spelling of that; callers
  // overriding backgroundColor inline (SensorTypeBadge) keep the white text.
  return (
    <span
      className={cx("badge badge-neutral rounded-full", className)}
      {...rest}
    >
      {children}
    </span>
  );
}

export function Well({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx("card bg-base-100 shadow-md", className)} {...rest}>
      <div className="card-body">{children}</div>
    </div>
  );
}

export function Jumbotron({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx("card bg-base-100 shadow-md", className)} {...rest}>
      <div className="card-body p-8">{children}</div>
    </div>
  );
}

type TableProps = {
  striped?: boolean;
} & React.TableHTMLAttributes<HTMLTableElement>;

export function Table({ striped, className, children, ...rest }: TableProps) {
  return (
    <table
      className={cx("table", striped && "table-zebra", className)}
      {...rest}
    >
      {children}
    </table>
  );
}

// Complete glyph -> FontAwesome 4.7 map. These are ALL glyphs in the app
// (survey 2026-07-10); FA is already the app's primary icon system.
const GLYPH_TO_FA: Record<string, string> = {
  home: "home",
  "shopping-cart": "shopping-cart",
  comment: "comment",
  dashboard: "tachometer",
  cog: "cog",
  "question-sign": "question-circle",
  "log-out": "sign-out",
  king: "trophy",
  tasks: "tasks",
  plus: "plus",
  briefcase: "briefcase",
  file: "file",
  bell: "bell",
  calendar: "calendar",
  print: "print",
  fullscreen: "arrows-alt",
  tag: "tag",
  download: "download",
  alert: "exclamation-triangle",
  envelope: "envelope",
  lock: "lock",
  "arrow-right": "arrow-right",
  "arrow-up": "arrow-up",
  "arrow-down": "arrow-down",
};

type GlyphiconProps = {
  glyph: string;
} & Omit<React.ComponentProps<typeof FontAwesome>, "name">;

export function Glyphicon({ glyph, className, ...rest }: GlyphiconProps) {
  return (
    <FontAwesome
      name={GLYPH_TO_FA[glyph] || glyph}
      className={className}
      {...rest}
    />
  );
}

// Grid/Row/Col emit bootstrap classnames PERMANENTLY (also after the daisyUI
// flip): 14 files hand-write raw col-* classes next to these, so the rung-3
// compat stylesheet owns the one and only grid implementation.
type GridProps = {
  fluid?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export function Grid({ fluid, className, children, ...rest }: GridProps) {
  return (
    <div
      className={cx(fluid ? "container-fluid" : "container", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Row({
  className,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx("row", className)} {...rest}>
      {children}
    </div>
  );
}

const COL_CLASS: Record<"xs" | "sm" | "md" | "lg", Record<number, string>> = {
  xs: { 4: "col-xs-4", 6: "col-xs-6", 8: "col-xs-8", 12: "col-xs-12" },
  sm: { 4: "col-sm-4", 6: "col-sm-6", 8: "col-sm-8", 12: "col-sm-12" },
  md: { 4: "col-md-4", 6: "col-md-6", 8: "col-md-8", 12: "col-md-12" },
  lg: { 4: "col-lg-4", 6: "col-lg-6", 8: "col-lg-8", 12: "col-lg-12" },
};

type ColProps = {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
} & React.HTMLAttributes<HTMLDivElement>;

export function Col({
  xs,
  sm,
  md,
  lg,
  className,
  children,
  ...rest
}: ColProps) {
  const classes = cx(
    xs && COL_CLASS.xs[xs],
    sm && COL_CLASS.sm[sm],
    md && COL_CLASS.md[md],
    lg && COL_CLASS.lg[lg],
    className,
  );
  return (
    <div className={classes} {...rest}>
      {children}
    </div>
  );
}
